import React from 'react';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../i18n/LocalizedLink';
import { usePageTitle } from '../hooks/usePageTitle';
import { SEOMeta } from '../templates/SEOMeta';
import { BaseLayout } from '../templates/BaseLayout';

const PrivacyPolicyEs: React.FC = () => {
  return (
    <BaseLayout>
      <SEOMeta
        title="Política de Privacidad - El Impostor Game"
        description="Política de privacidad de El Impostor Game. Información sobre cómo recopilamos, usamos y protegemos tus datos personales."
        canonical="https://el-impostor.com/privacy-policy"
        excludeAdsense={true}
      />
      <div className="min-h-screen bg-blue-50 dark:bg-gray-950 py-8 px-4">
        <div className="max-w-5xl mx-auto bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800">
          {/* Encabezado */}
          <div className="px-8 py-8 border-b border-gray-200 dark:border-gray-800">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3 tracking-tight">Política de Privacidad</h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Última actualización:{' '}
              {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Contenido */}
          <div className="px-8 py-8 space-y-8 text-gray-800 dark:text-gray-100 leading-relaxed text-left">
            {/* Introducción */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">1. Introducción</h2>
              <p className="text-justify">
                En <strong>El Impostor Game</strong>, operado por <strong>RurayLabs</strong>, nos comprometemos a proteger tu
                privacidad. Esta Política de Privacidad describe cómo recopilamos, usamos y protegemos tu información
                personal cuando utilizas nuestro sitio web y servicios.
              </p>
            </section>

            {/* Información que recopilamos */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">2. Información que Recopilamos</h2>

              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4">2.1 Información proporcionada por el usuario</h3>
                <p className="text-justify">
                  Recopilamos la información que nos proporcionas directamente cuando utilizas nuestros servicios, incluyendo:
                </p>
                <ul className="list-disc pl-8 space-y-1 text-gray-700 dark:text-gray-200 text-left">
                  <li>Nombre de usuario o apodo utilizado en el juego</li>
                  <li>Dirección de correo electrónico (cuando nos contactas)</li>
                  <li>Mensajes y contenido que nos envías</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-6">2.2 Información recopilada automáticamente</h3>
                <p className="text-justify">
                  Cuando accedes a nuestro sitio web, recopilamos automáticamente cierta información sobre tu dispositivo y tu uso
                  del servicio:
                </p>
                <ul className="list-disc pl-8 space-y-1 text-gray-700 dark:text-gray-200 text-left">
                  <li>Dirección IP y ubicación geográfica aproximada</li>
                  <li>Tipo de navegador y dispositivo</li>
                  <li>Páginas visitadas y tiempo de navegación</li>
                  <li>Cookies y tecnologías similares</li>
                  <li>Datos de uso del juego (partidas, estadísticas)</li>
                </ul>
              </div>
            </section>

            {/* Uso de la información */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">3. Cómo Utilizamos tu Información</h2>
              <p className="text-justify">La información que recopilamos se utiliza para los siguientes propósitos:</p>
              <ul className="list-disc pl-8 space-y-2 text-gray-700 dark:text-gray-200 text-left">
                <li>Proporcionar, mantener y mejorar nuestros servicios de juego</li>
                <li>Personalizar tu experiencia de usuario y ofrecer contenido relevante</li>
                <li>Responder a tus consultas y solicitudes de soporte técnico</li>
                <li>Analizar el uso del sitio para mejorar su funcionamiento y rendimiento</li>
                <li>Detectar y prevenir fraudes, actividades maliciosas o uso inadecuado</li>
                <li>Cumplir con obligaciones legales y regulatorias aplicables</li>
              </ul>
            </section>

            {/* Google AdSense y Publicidad */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">4. Publicidad y Google AdSense</h2>
              <p className="text-justify">
                Utilizamos <strong>Google AdSense</strong> para mostrar anuncios en nuestro sitio web. Google AdSense utiliza
                cookies y otras tecnologías de seguimiento para mostrar anuncios basados en tus visitas previas a nuestro sitio web
                y otros sitios de Internet. Esto permite ofrecer publicidad más relevante para ti y medir la eficacia de las
                campañas publicitarias.
              </p>

              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-6">4.1 Cookies de Google</h3>
                <p className="text-justify">
                  Google y sus socios publicitarios utilizan cookies para recopilar información sobre tus actividades en línea y
                  mostrar anuncios personalizados:
                </p>
                <ul className="list-disc pl-8 space-y-2 text-gray-700 dark:text-gray-200 text-left">
                  <li>Google utiliza cookies para publicar anuncios en nuestro sitio</li>
                  <li>Las cookies permiten a Google y sus socios mostrar anuncios basados en tu historial de navegación</li>
                  <li>
                    Puedes inhabilitar el uso de cookies de Google visitando la{' '}
                    <a
                      href="https://policies.google.com/technologies/ads"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline font-medium"
                    >
                      configuración de anuncios de Google
                    </a>
                  </li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-6">4.2 Proveedores de publicidad de terceros</h3>
                <p className="text-justify">
                  Los proveedores externos, incluido Google, utilizan cookies para publicar anuncios basados en las visitas previas
                  de los usuarios a nuestro sitio web o a otros sitios web. Estos proveedores pueden recopilar información sobre tus
                  visitas a este y otros sitios web para proporcionar anuncios sobre bienes y servicios que puedan ser de tu interés.
                  Esta práctica se conoce como publicidad basada en intereses o publicidad comportamental.
                </p>
              </div>
            </section>

            {/* Cookies */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">5. Cookies y Tecnologías Similares</h2>
              <p className="text-justify">
                Utilizamos cookies y tecnologías similares para mejorar tu experiencia en nuestro sitio web. Las cookies son
                pequeños archivos de texto almacenados en tu dispositivo que nos ayudan a proporcionar funcionalidades esenciales y
                mejorar nuestros servicios. Estas cookies nos permiten:
              </p>
              <ul className="list-disc pl-8 space-y-2 text-gray-700 dark:text-gray-200 text-left">
                <li>Recordar tus preferencias y configuraciones de usuario</li>
                <li>Mantener tu sesión de juego activa y segura</li>
                <li>Analizar el tráfico y uso del sitio mediante herramientas analíticas</li>
                <li>Mostrar publicidad relevante basada en tus intereses</li>
              </ul>
              <p className="text-justify mt-4">
                Puedes configurar tu navegador para rechazar todas las cookies o para que te avise cuando se envíe una cookie. Sin
                embargo, si no aceptas cookies, es posible que no puedas utilizar algunas funcionalidades de nuestro sitio web.
              </p>
            </section>

            {/* Compartir información */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">6. Compartir Información con Terceros</h2>
              <p className="text-justify">
                No vendemos, alquilamos ni comercializamos tu información personal a terceros. Sin embargo, podemos compartir tu
                información en las siguientes circunstancias:
              </p>
              <ul className="list-disc pl-8 space-y-2 text-gray-700 dark:text-gray-200 text-left">
                <li>
                  <strong>Google AdSense:</strong> Para mostrar anuncios personalizados y medir su efectividad según lo descrito en
                  la sección 4.
                </li>
                <li>
                  <strong>Proveedores de servicios:</strong> Compartimos información con empresas que nos ayudan a operar el sitio,
                  como servicios de hosting, análisis de datos y soporte técnico.
                </li>
                <li>
                  <strong>Autoridades legales:</strong> Cuando sea requerido por ley, orden judicial o cuando sea necesario para
                  proteger nuestros derechos, la seguridad de nuestros usuarios o el público en general.
                </li>
              </ul>
            </section>

            {/* Seguridad */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">7. Seguridad de los Datos</h2>
              <p className="text-justify">
                Implementamos medidas de seguridad técnicas, administrativas y organizativas razonables para proteger tu
                información personal contra acceso no autorizado, alteración, divulgación, pérdida o destrucción. Estas medidas
                incluyen cifrado de datos, controles de acceso y revisiones periódicas de nuestras prácticas de seguridad.
              </p>
              <p className="text-justify">
                Sin embargo, debes tener en cuenta que ningún método de transmisión por Internet o método de almacenamiento
                electrónico es 100% seguro. Aunque nos esforzamos por proteger tu información personal, no podemos garantizar su
                seguridad absoluta.
              </p>
            </section>

            {/* Retención de datos */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">8. Retención de Datos</h2>
              <p className="text-justify">
                Conservamos tu información personal solo durante el tiempo necesario para cumplir con los propósitos descritos en
                esta política, a menos que la ley requiera o permita un período de retención más largo. Los criterios utilizados
                para determinar nuestros períodos de retención incluyen la duración de nuestra relación contigo, si existe una
                obligación legal a la que estamos sujetos, y si la retención es aconsejable considerando nuestra posición legal.
              </p>
            </section>

            {/* Derechos del usuario */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">9. Tus Derechos</h2>
              <p className="text-justify">
                De acuerdo con las leyes de protección de datos aplicables, tienes los siguientes derechos respecto a tu
                información personal:
              </p>
              <ul className="list-disc pl-8 space-y-2 text-gray-700 dark:text-gray-200 text-left">
                <li>
                  <strong>Derecho de acceso:</strong> Solicitar información sobre los datos personales que tenemos sobre ti
                </li>
                <li>
                  <strong>Derecho de rectificación:</strong> Corregir datos incorrectos o incompletos
                </li>
                <li>
                  <strong>Derecho de supresión:</strong> Solicitar la eliminación de tus datos personales
                </li>
                <li>
                  <strong>Derecho de oposición:</strong> Oponerte al procesamiento de tus datos
                </li>
                <li>
                  <strong>Derecho de portabilidad:</strong> Recibir tus datos en un formato estructurado
                </li>
                <li>
                  <strong>Derecho de limitación:</strong> Solicitar la limitación del procesamiento de tus datos
                </li>
                <li>
                  <strong>Derecho a retirar el consentimiento:</strong> Retirar tu consentimiento en cualquier momento
                </li>
              </ul>
              <p className="text-justify mt-4">
                Para ejercer cualquiera de estos derechos, contáctanos a través de nuestra{' '}
                <LocalizedLink to="/contact" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline font-medium">
                  página de contacto
                </LocalizedLink>
                . Responderemos a tu solicitud dentro de los plazos establecidos por la ley.
              </p>
            </section>

            {/* Menores de edad */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">10. Menores de Edad</h2>
              <p className="text-justify">
                Nuestro servicio no está dirigido a menores de 13 años. No recopilamos intencionalmente información personal de
                menores de 13 años. Si eres padre o tutor legal y descubres que tu hijo nos ha proporcionado información personal
                sin tu consentimiento, contáctanos inmediatamente. Si descubrimos que hemos recopilado información personal de un
                menor de 13 años sin verificación del consentimiento parental, eliminaremos esa información de nuestros servidores
                de inmediato.
              </p>
            </section>

            {/* Cambios en la política */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">11. Cambios en esta Política</h2>
              <p className="text-justify">
                Podemos actualizar esta Política de Privacidad ocasionalmente para reflejar cambios en nuestras prácticas,
                tecnología, requisitos legales u otros factores. Te notificaremos sobre cambios significativos publicando la nueva
                política en esta página con una fecha de actualización revisada. Te recomendamos que revises periódicamente esta
                política para estar informado sobre cómo protegemos tu información. Tu uso continuado del servicio después de la
                publicación de cambios constituye tu aceptación de dichos cambios.
              </p>
            </section>

            {/* Información de contacto */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">12. Contacto</h2>
              <p className="text-justify">
                Si tienes preguntas, comentarios o inquietudes sobre esta Política de Privacidad o nuestras prácticas de datos,
                puedes contactarnos a través de los siguientes medios:
              </p>
              <ul className="list-disc pl-8 space-y-2 text-gray-700 dark:text-gray-200 text-left">
                <li>
                  A través de nuestra{' '}
                  <LocalizedLink to="/contact" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline font-medium">
                    página de contacto
                  </LocalizedLink>
                </li>
                <li>
                  Empresa: <strong>RurayLabs</strong>
                </li>
              </ul>
              <p className="text-justify mt-4">Nos esforzamos por responder a todas las consultas legítimas en un plazo de un mes.</p>
            </section>

            {/* Enlaces externos */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">13. Enlaces a Sitios de Terceros</h2>
              <p className="text-justify">
                Nuestro sitio web puede contener enlaces a sitios web de terceros que no son operados por nosotros. No tenemos
                control sobre, y no asumimos responsabilidad por el contenido, las políticas de privacidad o las prácticas de los
                sitios web de terceros. Te recomendamos enfáticamente que leas las políticas de privacidad de cada sitio web que
                visites antes de proporcionar cualquier información personal.
              </p>
            </section>

            {/* Información adicional para Google AdSense */}
            <section className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 p-6 space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">14. Información Adicional sobre Google AdSense</h2>
              <p className="text-justify">
                Para obtener más información detallada sobre cómo Google gestiona los datos en sus servicios de publicidad y cómo
                puedes controlar tu información, te recomendamos consultar los siguientes recursos oficiales de Google:
              </p>
              <ul className="list-disc pl-8 space-y-3 text-gray-700 dark:text-gray-200 text-left">
                <li>
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline font-medium"
                  >
                    Política de Privacidad de Google
                  </a>{' '}
                  - Información general sobre cómo Google recopila y usa datos
                </li>
                <li>
                  <a
                    href="https://policies.google.com/technologies/partner-sites"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline font-medium"
                  >
                    Cómo usa Google los datos cuando utilizas sitios web de nuestros partners
                  </a>{' '}
                  - Detalles específicos sobre el uso de datos en sitios asociados
                </li>
                <li>
                  <a
                    href="https://adssettings.google.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline font-medium"
                  >
                    Configuración de anuncios de Google
                  </a>{' '}
                  - Herramienta para gestionar tus preferencias de publicidad
                </li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

const PrivacyPolicyPt: React.FC = () => {
  return (
    <BaseLayout>
      <SEOMeta
        title="Política de Privacidade - El Impostor Game"
        description="Política de privacidade do El Impostor Game. Informações sobre como coletamos, usamos e protegemos seus dados pessoais."
        canonical="https://el-impostor.com/privacy-policy"
        excludeAdsense={true}
      />
      <div className="min-h-screen bg-blue-50 dark:bg-gray-950 py-8 px-4">
        <div className="max-w-5xl mx-auto bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800">
          {/* Encabezado */}
          <div className="px-8 py-8 border-b border-gray-200 dark:border-gray-800">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3 tracking-tight">Política de Privacidade</h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Última atualização:{' '}
              {new Date().toLocaleDateString('pt-BR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Conteúdo */}
          <div className="px-8 py-8 space-y-8 text-gray-800 dark:text-gray-100 leading-relaxed text-left">
            {/* Introdução */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">1. Introdução</h2>
              <p className="text-justify">
                No <strong>El Impostor Game</strong>, operado pela <strong>RurayLabs</strong>, estamos comprometidos em proteger sua
                privacidade. Esta Política de Privacidade descreve como coletamos, usamos e protegemos suas informações pessoais
                quando você utiliza nosso site e serviços.
              </p>
            </section>

            {/* Informações coletadas */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">2. Informações que coletamos</h2>

              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4">2.1 Informações fornecidas pelo usuário</h3>
                <p className="text-justify">
                  Coletamos as informações que você nos fornece diretamente quando utiliza nossos serviços, incluindo:
                </p>
                <ul className="list-disc pl-8 space-y-1 text-gray-700 dark:text-gray-200 text-left">
                  <li>Nome de usuário ou apelido utilizado no jogo</li>
                  <li>Endereço de e-mail (quando você entra em contato)</li>
                  <li>Mensagens e conteúdo que você nos envia</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-6">2.2 Informações coletadas automaticamente</h3>
                <p className="text-justify">
                  Quando você acessa nosso site, coletamos automaticamente algumas informações sobre seu dispositivo e seu uso do serviço:
                </p>
                <ul className="list-disc pl-8 space-y-1 text-gray-700 dark:text-gray-200 text-left">
                  <li>Endereço IP e localização geográfica aproximada</li>
                  <li>Tipo de navegador e dispositivo</li>
                  <li>Páginas visitadas e tempo de navegação</li>
                  <li>Cookies e tecnologias semelhantes</li>
                  <li>Dados de uso do jogo (partidas, estatísticas)</li>
                </ul>
              </div>
            </section>

            {/* Uso da informação */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">3. Como usamos suas informações</h2>
              <p className="text-justify">As informações coletadas são usadas para:</p>
              <ul className="list-disc pl-8 space-y-2 text-gray-700 dark:text-gray-200 text-left">
                <li>Fornecer, manter e melhorar nossos serviços de jogo</li>
                <li>Personalizar sua experiência e oferecer conteúdo relevante</li>
                <li>Responder às suas consultas e solicitações de suporte</li>
                <li>Analisar o uso do site para melhorar desempenho</li>
                <li>Detectar e prevenir fraudes ou uso inadequado</li>
                <li>Cumprir obrigações legais e regulatórias aplicáveis</li>
              </ul>
            </section>

            {/* Compartilhamento */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">4. Compartilhamento de informações</h2>
              <p className="text-justify">
                Não vendemos suas informações pessoais. Podemos compartilhar dados com provedores de serviços que nos ajudam a operar o site,
                sempre com contratos de confidencialidade. Também podemos compartilhar informações quando exigido por lei.
              </p>
            </section>

            {/* Cookies */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">5. Cookies e tecnologias semelhantes</h2>
              <p className="text-justify">
                Utilizamos cookies para melhorar a experiência do usuário, analisar o tráfego e personalizar conteúdo. Você pode gerenciar
                suas preferências no navegador, mas isso pode afetar algumas funcionalidades do site.
              </p>
            </section>

            {/* Segurança */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">6. Segurança</h2>
              <p className="text-justify">
                Adotamos medidas técnicas e organizacionais para proteger suas informações. No entanto, nenhum sistema é 100% seguro e
                não podemos garantir segurança absoluta.
              </p>
            </section>

            {/* Direitos */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">7. Seus direitos</h2>
              <p className="text-justify">
                Você pode solicitar acesso, correção ou exclusão de seus dados pessoais, bem como limitar ou se opor ao processamento.
                Para isso, entre em contato conosco.
              </p>
            </section>

            {/* Alterações */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">8. Alterações nesta política</h2>
              <p className="text-justify">
                Podemos atualizar esta Política de Privacidade periodicamente. Alterações relevantes serão comunicadas nesta página.
              </p>
            </section>

            {/* Contato */}
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">9. Contato</h2>
              <p className="text-justify">
                Se você tiver dúvidas sobre esta Política de Privacidade, entre em contato conosco.
              </p>
              <div className="flex flex-wrap gap-3">
                <LocalizedLink to="/contact" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline text-sm font-medium">
                  Contato
                </LocalizedLink>
                <LocalizedLink to="/" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline text-sm font-medium">
                  Voltar ao início
                </LocalizedLink>
              </div>
            </section>
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

const PrivacyPolicyEn: React.FC = () => {
  return (
    <BaseLayout>
      <SEOMeta
        title="Privacy Policy - Impostor Game"
        description="Privacy policy for Impostor Game. Learn how we collect, use, and protect your personal data."
        canonical="https://el-impostor.com/privacy-policy"
        excludeAdsense={true}
      />
      <div className="min-h-screen bg-blue-50 dark:bg-gray-950 py-8 px-4">
        <div className="max-w-5xl mx-auto bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800">
          {/* Header */}
          <div className="px-8 py-8 border-b border-gray-200 dark:border-gray-800">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3 tracking-tight">Privacy Policy</h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Last updated:{' '}
              {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Content */}
          <div className="px-8 py-8 space-y-8 text-gray-800 dark:text-gray-100 leading-relaxed text-left">
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">1. Introduction</h2>
              <p className="text-justify">
                At <strong>El Impostor Game</strong>, operated by <strong>RurayLabs</strong>, we are committed to protecting your
                privacy. This Privacy Policy describes how we collect, use, and protect your personal information when you use our
                website and services.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">2. Information We Collect</h2>

              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4">2.1 Information provided by you</h3>
                <p className="text-justify">We collect information you provide directly when you use our services, including:</p>
                <ul className="list-disc pl-8 space-y-1 text-gray-700 dark:text-gray-200 text-left">
                  <li>Username or nickname used in the game</li>
                  <li>Email address (when you contact us)</li>
                  <li>Messages and content you send us</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-6">2.2 Information collected automatically</h3>
                <p className="text-justify">
                  When you access our website, we automatically collect certain information about your device and how you use the
                  service:
                </p>
                <ul className="list-disc pl-8 space-y-1 text-gray-700 dark:text-gray-200 text-left">
                  <li>IP address and approximate geographic location</li>
                  <li>Browser and device type</li>
                  <li>Pages visited and browsing time</li>
                  <li>Cookies and similar technologies</li>
                  <li>Game usage data (matches, statistics)</li>
                </ul>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">3. How We Use Your Information</h2>
              <p className="text-justify">We use the information we collect for the following purposes:</p>
              <ul className="list-disc pl-8 space-y-2 text-gray-700 dark:text-gray-200 text-left">
                <li>Provide, maintain, and improve our game services</li>
                <li>Personalize your user experience and offer relevant content</li>
                <li>Respond to your inquiries and support requests</li>
                <li>Analyze site usage to improve performance and functionality</li>
                <li>Detect and prevent fraud, malicious activity, or misuse</li>
                <li>Comply with applicable legal and regulatory obligations</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">4. Advertising and Google AdSense</h2>
              <p className="text-justify">
                We use <strong>Google AdSense</strong> to display ads on our website. Google AdSense uses cookies and other tracking
                technologies to show ads based on your previous visits to our website and other websites on the Internet. This makes
                it possible to offer more relevant advertising and measure the effectiveness of ad campaigns.
              </p>

              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-6">4.1 Google cookies</h3>
                <p className="text-justify">
                  Google and its advertising partners use cookies to collect information about your online activities and display
                  personalized ads:
                </p>
                <ul className="list-disc pl-8 space-y-2 text-gray-700 dark:text-gray-200 text-left">
                  <li>Google uses cookies to serve ads on our site</li>
                  <li>Cookies enable Google and its partners to show ads based on your browsing history</li>
                  <li>
                    You can disable the use of Google cookies by visiting the{' '}
                    <a
                      href="https://policies.google.com/technologies/ads"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline font-medium"
                    >
                      Google ads settings
                    </a>
                  </li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-6">4.2 Third-party advertising providers</h3>
                <p className="text-justify">
                  Third-party vendors, including Google, use cookies to serve ads based on users' prior visits to our website or
                  other websites. These vendors may collect information about your visits to this and other websites to provide ads
                  about goods and services that may be of interest to you. This practice is known as interest-based advertising or
                  behavioral advertising.
                </p>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">5. Cookies and Similar Technologies</h2>
              <p className="text-justify">
                We use cookies and similar technologies to improve your experience on our website. Cookies are small text files
                stored on your device that help us provide essential functionality and improve our services. These cookies allow us
                to:
              </p>
              <ul className="list-disc pl-8 space-y-2 text-gray-700 dark:text-gray-200 text-left">
                <li>Remember your preferences and user settings</li>
                <li>Keep your game session active and secure</li>
                <li>Analyze site traffic and usage via analytics tools</li>
                <li>Show relevant advertising based on your interests</li>
              </ul>
              <p className="text-justify mt-4">
                You can configure your browser to refuse all cookies or to notify you when a cookie is being sent. However, if you
                do not accept cookies, you may not be able to use certain features of our website.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">6. Sharing Information with Third Parties</h2>
              <p className="text-justify">
                We do not sell, rent, or trade your personal information to third parties. However, we may share your information
                in the following circumstances:
              </p>
              <ul className="list-disc pl-8 space-y-2 text-gray-700 dark:text-gray-200 text-left">
                <li>
                  <strong>Google AdSense:</strong> To display personalized ads and measure their effectiveness as described in
                  section 4.
                </li>
                <li>
                  <strong>Service providers:</strong> We share information with companies that help us operate the site, such as
                  hosting, data analytics, and technical support providers.
                </li>
                <li>
                  <strong>Legal authorities:</strong> When required by law, court order, or when necessary to protect our rights,
                  the safety of our users, or the public.
                </li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">7. Data Security</h2>
              <p className="text-justify">
                We implement reasonable technical, administrative, and organizational security measures to protect your personal
                information against unauthorized access, alteration, disclosure, loss, or destruction. These measures include data
                encryption, access controls, and periodic reviews of our security practices.
              </p>
              <p className="text-justify">
                However, please note that no method of transmission over the Internet or method of electronic storage is 100% secure.
                While we strive to protect your personal information, we cannot guarantee its absolute security.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">8. Data Retention</h2>
              <p className="text-justify">
                We retain your personal information only for as long as necessary to fulfill the purposes described in this policy,
                unless a longer retention period is required or permitted by law. The criteria used to determine our retention
                periods include the duration of our relationship with you, whether there is a legal obligation we are subject to,
                and whether retention is advisable considering our legal position.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">9. Your Rights</h2>
              <p className="text-justify">
                In accordance with applicable data protection laws, you have the following rights regarding your personal
                information:
              </p>
              <ul className="list-disc pl-8 space-y-2 text-gray-700 dark:text-gray-200 text-left">
                <li>
                  <strong>Right of access:</strong> Request information about the personal data we hold about you
                </li>
                <li>
                  <strong>Right to rectification:</strong> Correct inaccurate or incomplete data
                </li>
                <li>
                  <strong>Right to erasure:</strong> Request the deletion of your personal data
                </li>
                <li>
                  <strong>Right to object:</strong> Object to the processing of your data
                </li>
                <li>
                  <strong>Right to data portability:</strong> Receive your data in a structured format
                </li>
                <li>
                  <strong>Right to restriction:</strong> Request restriction of the processing of your data
                </li>
                <li>
                  <strong>Right to withdraw consent:</strong> Withdraw your consent at any time
                </li>
              </ul>
              <p className="text-justify mt-4">
                To exercise any of these rights, contact us through our{' '}
                <LocalizedLink to="/contact" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline font-medium">
                  contact page
                </LocalizedLink>
                . We will respond within the timeframes established by law.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">10. Minors</h2>
              <p className="text-justify">
                Our service is not directed to children under 13. We do not knowingly collect personal information from children
                under 13. If you are a parent or legal guardian and you discover that your child has provided us with personal
                information without your consent, please contact us immediately. If we become aware that we have collected personal
                information from a child under 13 without parental consent verification, we will delete that information from our
                servers immediately.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">11. Changes to this Policy</h2>
              <p className="text-justify">
                We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal
                requirements, or other factors. We will notify you of significant changes by posting the new policy on this page
                with a revised update date. We encourage you to review this policy periodically to stay informed about how we
                protect your information. Your continued use of the service after changes are posted constitutes your acceptance of
                those changes.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">12. Contact</h2>
              <p className="text-justify">
                If you have questions, comments, or concerns about this Privacy Policy or our data practices, you can contact us
                through the following means:
              </p>
              <ul className="list-disc pl-8 space-y-2 text-gray-700 dark:text-gray-200 text-left">
                <li>
                  Through our{' '}
                  <LocalizedLink to="/contact" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline font-medium">
                    contact page
                  </LocalizedLink>
                </li>
                <li>
                  Company: <strong>RurayLabs</strong>
                </li>
              </ul>
              <p className="text-justify mt-4">We strive to respond to all legitimate inquiries within one month.</p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">13. Links to Third-Party Sites</h2>
              <p className="text-justify">
                Our website may contain links to third-party websites that are not operated by us. We have no control over, and
                assume no responsibility for, the content, privacy policies, or practices of any third-party websites. We strongly
                encourage you to read the privacy policies of every website you visit before providing any personal information.
              </p>
            </section>

            <section className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 p-6 space-y-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">14. Additional Information about Google AdSense</h2>
              <p className="text-justify">
                For more detailed information about how Google manages data in its advertising services and how you can control
                your information, we recommend reviewing the following official Google resources:
              </p>
              <ul className="list-disc pl-8 space-y-3 text-gray-700 dark:text-gray-200 text-left">
                <li>
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline font-medium"
                  >
                    Google Privacy Policy
                  </a>{' '}
                  - General information about how Google collects and uses data
                </li>
                <li>
                  <a
                    href="https://policies.google.com/technologies/partner-sites"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline font-medium"
                  >
                    How Google uses data when you use partner sites
                  </a>{' '}
                  - Specific details about data usage on partner sites
                </li>
                <li>
                  <a
                    href="https://adssettings.google.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline font-medium"
                  >
                    Google Ads Settings
                  </a>{' '}
                  - Tool to manage your advertising preferences
                </li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

const PrivacyPolicy: React.FC = () => {
  const { i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const isPt = i18n.language === 'pt';

  usePageTitle(isEn ? 'Privacy Policy - Impostor Game' : isPt ? 'Política de Privacidade - Impostor Game' : 'Política de Privacidad - El Impostor Game');

  if (isEn) return <PrivacyPolicyEn />;
  if (isPt) return <PrivacyPolicyPt />;
  return <PrivacyPolicyEs />;
};

export default PrivacyPolicy;
